import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../context/AuthContext';
import { AdminLayout } from '../components/AdminLayout';
import { toast } from 'sonner';
import { escapeHtml } from '../utils/sanitize';
import { 
  BookOpen, Palette, Users, Plus, Edit2, Trash2, X, Save, 
  RefreshCw, Shield, LogOut, ChevronDown, ChevronUp, Calendar, ClipboardList, Image, Pause, Play,
  RotateCcw, History, AlertTriangle, GripVertical, CalendarPlus, Rocket, Share2
} from 'lucide-react';
import { NextTermModal } from '../components/NextTermModal';
import { ShareRosterModal } from '../components/ShareRosterModal';
import { PrivateLessonSettings } from '../components/PrivateLessonSettings';
import { ProgramListingSettings } from '../components/ProgramListingSettings';
import { MediaPicker } from '../components/MediaPicker';
import { isPrivateLesson } from '../utils/privateLesson';
import { mediaUrl } from '../utils/mediaUrl';

const API_URL = "";

export const AdminContent = () => {
  const { user, token, logout, isAdmin, isAuthenticated, loading: authLoading } = useAuth();
  const [activeTab, setActiveTab] = useState('programs');
  const [programs, setPrograms] = useState([]);
  const [workshops, setWorkshops] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingItem, setEditingItem] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showAddModal, setShowAddModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [enrollmentModal, setEnrollmentModal] = useState(null);
  const [shareRoster, setShareRoster] = useState(null);
  const [enrollmentData, setEnrollmentData] = useState(null);
  const [enrollmentLoading, setEnrollmentLoading] = useState(false);
  const [addStudentForm, setAddStudentForm] = useState({ student_name: '', student_email: '', amount_paid: 0, notes: '' });
  const [pauseConfirm, setPauseConfirm] = useState(null);    // { program, action: 'pause'|'reopen' }
  const [pauseSubmitting, setPauseSubmitting] = useState(false);
  // Refund + term reset
  const [rosterTerm, setRosterTerm] = useState('current');
  const [termHistory, setTermHistory] = useState([]);
  const [refundModal, setRefundModal] = useState(null);      // { student }
  const [refundForm, setRefundForm] = useState({ mode: 'cancel_only', reason: '', send_email: true });
  const [refundSubmitting, setRefundSubmitting] = useState(false);
  const [newTermModal, setNewTermModal] = useState(null);
  const [newTermForm, setNewTermForm] = useState({ previous_term_label: '', new_term_label: '', new_schedule: '', new_duration: '' });
  const [newTermSubmitting, setNewTermSubmitting] = useState(false);
  // Instructor drag-and-drop reorder state
  const [draggedInstructorIndex, setDraggedInstructorIndex] = useState(null);
  const [instructorReorderSaving, setInstructorReorderSaving] = useState(false);
  // Next-term rollover
  const [nextTerm, setNextTerm] = useState(null);             // { singleProgram } | {}
  const [publishing, setPublishing] = useState(null);         // program id being published
  const navigate = useNavigate();

  useEffect(() => {
    if (token && isAdmin) {
      fetchData();
    }
  }, [token, isAdmin]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [programsRes, workshopsRes, instructorsRes, enrollmentsRes] = await Promise.all([
        fetch(`${API_URL}/api/content/admin/programs?state=current`, { headers: { Authorization: `Bearer ${token}` }, cache: 'no-store' }),
        fetch(`${API_URL}/api/content/workshops`),
        fetch(`${API_URL}/api/content/instructors`),
        fetch(`${API_URL}/api/content/enrollments`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (programsRes.ok) {
        const list = await programsRes.json();
        // Drafts (next term being prepared) float to the top
        list.sort((a, b) => {
          const da = (a.status || 'active') === 'draft' ? 0 : 1;
          const dbb = (b.status || 'active') === 'draft' ? 0 : 1;
          return da !== dbb ? da - dbb : a.id - b.id;
        });
        setPrograms(list);
      }
      if (workshopsRes.ok) setWorkshops(await workshopsRes.json());
      if (instructorsRes.ok) setInstructors(await instructorsRes.json());
      if (enrollmentsRes.ok) setEnrollments(await enrollmentsRes.json());
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load content data');
    } finally {
      setLoading(false);
    }
  };

  const getEnrollmentCount = (type, id) => {
    const key = `${type}_${String(id)}`;
    const enrollment = enrollments.find(e => e.id === key);
    return enrollment?.count || 0;
  };

  // Enrollment Management Functions
  const handleManageEnrollment = async (program, termOverride = null) => {
    setEnrollmentModal(program);
    setEnrollmentLoading(true);
    const term = termOverride !== null ? termOverride : 'current';
    if (termOverride === null) setRosterTerm('current');
    setAddStudentForm({ student_name: '', student_email: '', amount_paid: program.price || 0, notes: '' });

    try {
      const [enrollRes, historyRes] = await Promise.all([
        fetch(`${API_URL}/api/content/programs/${program.id}/enrollment?term=${encodeURIComponent(term)}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/content/programs/${program.id}/term-history`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      if (enrollRes.ok) {
        setEnrollmentData(await enrollRes.json());
      } else {
        toast.error('Failed to load enrollment data');
      }
      if (historyRes.ok) {
        const h = await historyRes.json();
        setTermHistory(h.terms || []);
      }
    } catch (error) {
      console.error('Error fetching enrollment:', error);
      toast.error('Error loading enrollment data');
    } finally {
      setEnrollmentLoading(false);
    }
  };

  const handleAddStudent = async () => {
    if (!addStudentForm.student_name || !addStudentForm.student_email) {
      toast.error('Student name and email are required');
      return;
    }
    
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/content/programs/${enrollmentModal.id}/enrollment/add`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(addStudentForm)
      });
      
      if (res.ok) {
        toast.success('Student added successfully');
        setAddStudentForm({ student_name: '', student_email: '', amount_paid: enrollmentModal.price || 0, notes: '' });
        handleManageEnrollment(enrollmentModal); // Refresh data
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to add student');
      }
    } catch (error) {
      toast.error('Error adding student');
    } finally {
      setSaving(false);
    }
  };

  const openRefundModal = (student) => {
    const hasPaypal = !!(student.paypal_capture_id || (student.payment_id && (student.amount_paid || 0) > 0));
    setRefundModal({ student });
    setRefundForm({
      mode: hasPaypal ? 'paypal_auto' : ((student.amount_paid || 0) > 0 ? 'manual' : 'cancel_only'),
      reason: '',
      send_email: true,
    });
  };

  const handleRefundSubmit = async () => {
    if (!refundModal || !enrollmentModal) return;
    setRefundSubmitting(true);
    try {
      const res = await fetch(
        `${API_URL}/api/content/programs/${enrollmentModal.id}/enrollment/${refundModal.student.id}/refund`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(refundForm),
        }
      );
      const body = await res.json().catch(() => ({}));
      if (res.ok && body.success) {
        toast.success(
          refundForm.mode === 'paypal_auto'
            ? `Refunded $${body.amount_refunded} via PayPal`
            : refundForm.mode === 'manual'
              ? 'Marked as refunded (manual). Refund issued offline.'
              : 'Removed from roster (no refund needed)'
        );
        setRefundModal(null);
        handleManageEnrollment(enrollmentModal, rosterTerm);
      } else {
        toast.error(body.detail || body.message || 'Refund failed');
      }
    } catch (e) {
      toast.error('Network error');
    } finally {
      setRefundSubmitting(false);
    }
  };

  const openNewTermModal = () => {
    const year = new Date().getFullYear();
    setNewTermForm({
      previous_term_label: enrollmentModal?.current_term_label || `${enrollmentModal?.title || 'Class'} - ${year}`,
      new_term_label: '',
      new_schedule: '',
      new_duration: '',
    });
    setNewTermModal(enrollmentModal);
  };

  const handleStartNewTerm = async () => {
    if (!newTermModal) return;
    if (!newTermForm.previous_term_label.trim()) {
      toast.error('Please label the term being closed (e.g. "Spring 2026")');
      return;
    }
    setNewTermSubmitting(true);
    try {
      const res = await fetch(
        `${API_URL}/api/content/programs/${newTermModal.id}/start-new-term`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(newTermForm),
        }
      );
      const body = await res.json().catch(() => ({}));
      if (res.ok && body.success) {
        toast.success(`Archived ${body.archived_count} student(s) under "${body.previous_term_label}". Roster reset.`);
        setNewTermModal(null);
        handleManageEnrollment(enrollmentModal, 'current');
        fetchData();
      } else {
        toast.error(body.detail || 'Failed to start new term');
      }
    } catch (e) {
      toast.error('Network error');
    } finally {
      setNewTermSubmitting(false);
    }
  };

  const handlePrintRoster = () => {
    if (!enrollmentModal || !enrollmentData) return;
    const regs = (enrollmentData.registrations || []).filter(r => r.status !== 'removed');
    // Escape every interpolated value — student/instructor/program text can
    // contain arbitrary user-submitted content (registration forms).
    const E = escapeHtml;
    const title = E(enrollmentModal.title);
    const instructor = E(enrollmentModal.instructor || '—');
    const schedule = E(enrollmentModal.schedule || '—');
    const price = E(String(enrollmentModal.price || 0));
    const printedOn = E(new Date().toLocaleDateString('en-US', { year:'numeric', month:'long', day:'numeric' }));
    const printWindow = window.open('', '_blank', 'width=900,height=700');
    printWindow.document.write(`<!DOCTYPE html>
<html>
<head>
  <title>Class Roster — ${title}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: Arial, sans-serif; font-size: 12px; color: #111; padding: 28px; }
    .header { border-bottom: 2px solid #1a1a1a; padding-bottom: 12px; margin-bottom: 18px; display: flex; justify-content: space-between; align-items: flex-end; }
    .academy-name { font-size: 18px; font-weight: bold; letter-spacing: 0.05em; }
    .academy-sub { font-size: 10px; color: #555; margin-top: 2px; }
    .class-info { text-align: right; font-size: 11px; color: #333; }
    .class-title { font-size: 16px; font-weight: bold; margin-bottom: 12px; }
    .meta { display: flex; gap: 32px; margin-bottom: 18px; font-size: 11px; color: #444; }
    .meta strong { color: #111; }
    .count { font-size: 11px; color: #555; margin-bottom: 8px; }
    table { width: 100%; border-collapse: collapse; }
    thead th { background: #1a1a1a; color: #fff; padding: 7px 8px; text-align: left; font-size: 11px; text-transform: uppercase; letter-spacing: 0.04em; }
    thead th.center { text-align: center; }
    tbody tr { border-bottom: 1px solid #ddd; }
    tbody tr:nth-child(even) { background: #f9f9f9; }
    tbody td { padding: 8px; vertical-align: middle; font-size: 11px; }
    td.center { text-align: center; }
    .checkbox { width: 18px; height: 18px; border: 1.5px solid #555; display: inline-block; border-radius: 2px; }
    .footer { margin-top: 24px; border-top: 1px solid #ccc; padding-top: 10px; font-size: 10px; color: #777; display: flex; justify-content: space-between; }
    @media print { body { padding: 18px; } button { display: none; } }
  </style>
</head>
<body>
  <div class="header">
    <div>
      <div class="academy-name">The Coppini Academy of Fine Art</div>
      <div class="academy-sub">115 Melrose Place, San Antonio, TX 78212 &nbsp;|&nbsp; (210) 530-8445</div>
    </div>
    <div class="class-info">Printed: ${printedOn}</div>
  </div>

  <div class="class-title">${title}</div>
  <div class="meta">
    <div><strong>Instructor:</strong> ${instructor}</div>
    <div><strong>Schedule:</strong> ${schedule}</div>
    <div><strong>Price:</strong> $${price}</div>
  </div>
  <div class="count">Total enrolled: <strong>${regs.length}</strong></div>

  <table>
    <thead>
      <tr>
        <th style="width:28px">#</th>
        <th>Student Name</th>
        <th>Phone</th>
        <th>Email</th>
        <th style="width:60px">Paid</th>
        <th class="center" style="width:56px">Present</th>
      </tr>
    </thead>
    <tbody>
      ${regs.map((r, i) => `
      <tr>
        <td>${i + 1}</td>
        <td><strong>${E(r.student_name || '—')}</strong></td>
        <td>${E(r.registration_data?.phone || r.phone || '—')}</td>
        <td>${E(r.student_email || '—')}</td>
        <td>$${E(((r.amount_paid || 0)).toFixed(2))}</td>
        <td class="center"><span class="checkbox"></span></td>
      </tr>`).join('')}
      ${Array.from({ length: Math.max(0, 4 - regs.length) }, (_, i) => `
      <tr>
        <td>${regs.length + i + 1}</td>
        <td></td><td></td><td></td><td></td>
        <td class="center"><span class="checkbox"></span></td>
      </tr>`).join('')}
    </tbody>
  </table>

  <div class="footer">
    <span>Coppini Academy of Fine Art &mdash; Class Roster</span>
    <span>${title} &nbsp;|&nbsp; ${regs.length} student${regs.length !== 1 ? 's' : ''} enrolled</span>
  </div>
  <script>window.onload = () => window.print();</script>
</body>
</html>`);
    printWindow.document.close();
  };

  const handleEdit = (item, type) => {
    setEditingItem({ ...item, type });
    setEditForm({ ...item });
  };

  const handlePauseProgram = async () => {
    if (!pauseConfirm) return;
    setPauseSubmitting(true);
    try {
      const action = pauseConfirm.action;            // 'pause' | 'reopen'
      const program = pauseConfirm.program;
      const res = await fetch(`${API_URL}/api/content/programs/${program.id}/${action}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (res.ok) {
        toast.success(action === 'pause' ? 'Program paused' : 'Program reopened');
        setPauseConfirm(null);
        fetchData();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Action failed');
      }
    } catch (e) {
      toast.error('Network error');
    } finally {
      setPauseSubmitting(false);
    }
  };

  const handlePublishProgram = async (program) => {
    setPublishing(program.id);
    try {
      const res = await fetch(`${API_URL}/api/content/programs/${program.id}/publish`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        toast.success(`"${program.title}" is now live and open for registration`);
        fetchData();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to publish program');
      }
    } catch (e) {
      toast.error('Network error');
    } finally {
      setPublishing(null);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    const type = editingItem.type;
    const id = editingItem.id;
    
    try {
      const endpoint = type === 'program' ? 'programs' : type === 'workshop' ? 'workshops' : 'instructors';
      const response = await fetch(`${API_URL}/api/content/${endpoint}/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editForm)
      });

      if (response.ok) {
        toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} updated`);
        setEditingItem(null);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update');
      }
    } catch (error) {
      toast.error('Error updating');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (type, id, name) => {
    if (!confirm(`Delete "${name}"? This cannot be undone.`)) return;

    try {
      const endpoint = type === 'program' ? 'programs' : type === 'workshop' ? 'workshops' : 'instructors';
      const response = await fetch(`${API_URL}/api/content/${endpoint}/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} deleted`);
        fetchData();
      } else {
        toast.error('Failed to delete');
      }
    } catch (error) {
      toast.error('Error deleting');
    }
  };

  // Persist a new instructor order to the server. Optimistic local update; reverts on error.
  const persistInstructorOrder = async (newList) => {
    const previous = instructors;
    setInstructors(newList);
    setInstructorReorderSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/content/instructors/reorder`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ ordered_ids: newList.map(i => i.id) })
      });
      if (!response.ok) throw new Error('Reorder failed');
      toast.success('Instructor order saved');
    } catch (err) {
      console.error('Reorder error:', err);
      toast.error('Failed to save order — reverted');
      setInstructors(previous);
    } finally {
      setInstructorReorderSaving(false);
    }
  };

  const moveInstructor = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= instructors.length) return;
    const newList = [...instructors];
    [newList[index], newList[newIndex]] = [newList[newIndex], newList[index]];
    persistInstructorOrder(newList);
  };

  const handleInstructorDragStart = (index) => setDraggedInstructorIndex(index);
  const handleInstructorDragOver = (e) => { e.preventDefault(); };
  const handleInstructorDrop = (targetIndex) => {
    if (draggedInstructorIndex === null || draggedInstructorIndex === targetIndex) {
      setDraggedInstructorIndex(null);
      return;
    }
    const newList = [...instructors];
    const [moved] = newList.splice(draggedInstructorIndex, 1);
    newList.splice(targetIndex, 0, moved);
    setDraggedInstructorIndex(null);
    persistInstructorOrder(newList);
  };

  const handleAdd = async () => {
    setSaving(true);
    const type = activeTab === 'programs' ? 'program' : activeTab === 'workshops' ? 'workshop' : 'instructor';
    
    try {
      const endpoint = activeTab;
      const response = await fetch(`${API_URL}/api/content/${endpoint}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editForm)
      });

      if (response.ok) {
        toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} created`);
        setShowAddModal(false);
        setEditForm({});
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create');
      }
    } catch (error) {
      toast.error('Error creating');
    } finally {
      setSaving(false);
    }
  };

  const openAddModal = () => {
    if (activeTab === 'programs') {
      setEditForm({
        title: '', category: 'Painting', level: 'All Levels', description: '',
        duration: '', price: 0, schedule: '', instructor: '', instructorEmail: '',
        class_type: 'informational', capacity: null, instructorImage: '', classImage: '',
        payment_method: 'instructor', isPayPal: false
      });
    } else if (activeTab === 'workshops') {
      setEditForm({
        title: '', instructor: '', date: '', endDate: '', schedule: '', time: '',
        description: '', level: 'All Levels', price: 0, memberPrice: 0, nonMemberPrice: 0,
        materials: '', location: '115 Melrose Place, San Antonio, TX'
      });
    } else {
      setEditForm({
        name: '', specialty: '', bio: '', imageUrl: ''
      });
    }
    setShowAddModal(true);
  };

  const handleReseedLeadership = async () => {
    if (!confirm('Reseed the Executive Board and Chair Positions from the code? This will overwrite the current Leadership data with the version defined in the deployed code. Programs, workshops, instructors, and members are NOT affected.')) return;
    try {
      const response = await fetch(`${API_URL}/api/content/leadership/reseed`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Reseed failed');
      const data = await response.json();
      toast.success(`Leadership reseeded — ${data.executive_count} exec + ${data.chair_count} chair`);
    } catch (err) {
      console.error('Reseed leadership error:', err);
      toast.error('Failed to reseed leadership');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (authLoading) {
    return null; // AdminLayout handles loading
  }

  return (
    <AdminLayout title="Class & Content Management" subtitle="Manage programs, instructors, and content">
      <div data-testid="admin-content-page">
      {/* Tabs */}
      <section className="py-4 border-b border-stone-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant={activeTab === 'programs' ? 'default' : 'outline'}
              onClick={() => setActiveTab('programs')}
              className={activeTab === 'programs' ? 'bg-cafa-red' : ''}
            >
              <BookOpen size={16} className="mr-2" />
              Programs ({programs.length})
            </Button>
            <Button
              variant={activeTab === 'instructors' ? 'default' : 'outline'}
              onClick={() => setActiveTab('instructors')}
              className={activeTab === 'instructors' ? 'bg-cafa-red' : ''}
            >
              <Users size={16} className="mr-2" />
              Instructors ({instructors.length})
            </Button>
            <Button
              variant={activeTab === 'enrollments' ? 'default' : 'outline'}
              onClick={() => setActiveTab('enrollments')}
              className={activeTab === 'enrollments' ? 'bg-cafa-red' : ''}
            >
              <Calendar size={16} className="mr-2" />
              Enrollments
            </Button>
            
            <div className="ml-auto flex gap-2">
              <Button variant="outline" onClick={fetchData} size="sm">
                <RefreshCw size={16} className="mr-1" /> Refresh
              </Button>
              <Button
                variant="outline"
                onClick={handleReseedLeadership}
                size="sm"
                title="Overwrite the About page Executive Board & Chair Positions with the values from the deployed code"
                data-testid="reseed-leadership-btn"
              >
                <Users size={16} className="mr-1" /> Sync Leadership
              </Button>
              {activeTab === 'programs' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setNextTerm({ singleProgram: null })}
                  className="border-cafa-red text-cafa-red hover:bg-cafa-red-50"
                  title="Copy existing classes forward into a new term as drafts"
                  data-testid="setup-next-term-btn"
                >
                  <CalendarPlus size={16} className="mr-1" /> Set Up Next Term
                </Button>
              )}
              {activeTab !== 'enrollments' && (
                <Button onClick={openAddModal} size="sm" className="bg-cafa-red hover:bg-cafa-red-900 text-white">
                  <Plus size={16} className="mr-1" /> Add New
                </Button>
              )}
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cafa-red"></div>
          </div>
        ) : (
          <>
            {/* PROGRAMS TAB */}
            {activeTab === 'programs' && (
              <div className="grid gap-4">
                {programs.map(program => (
                  <Card key={program.id} className="border-stone-200">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-3">
                        {/* Thumbnail (prefers class image, falls back to instructorImage) */}
                        {(program.classImage || program.instructorImage) ? (
                          <img src={mediaUrl(program.classImage || program.instructorImage, 'thumb')} alt={program.title} className="w-16 h-16 rounded object-cover flex-shrink-0 border border-stone-200" />
                        ) : (
                          <div className="w-16 h-16 rounded bg-stone-100 flex items-center justify-center flex-shrink-0 border border-stone-200">
                            <Image size={20} className="text-stone-400" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-semibold text-stone-900">{program.title}</h3>
                            <span className="px-2 py-0.5 bg-stone-100 text-stone-600 text-xs rounded">{program.category}</span>
                            <span className="px-2 py-0.5 bg-cafa-red-100 text-cafa-red-900 text-xs rounded">{(program.level || '').replace(/\bIntermidate\b/gi, 'Intermediate').replace(/\bBeginners\b/g, 'Beginner')}</span>
                            {program.isPayPal === true && (
                              <span className="px-2 py-0.5 bg-blue-100 text-blue-800 border border-blue-300 text-xs rounded font-medium"
                                title="Open Session — listed on the Open Sessions page with member / non-member drop-in pricing"
                                data-testid={`open-session-badge-${program.id}`}>
                                Open Session
                              </span>
                            )}
                            {isPrivateLesson(program) && (
                              <span className="px-2 py-0.5 bg-cafa-red-100 text-cafa-red-900 border border-cafa-red/30 text-xs rounded font-medium"
                                title="Private lesson — booked by appointment, no fixed day or time"
                                data-testid={`private-badge-${program.id}`}>
                                Private Lesson
                              </span>
                            )}
                            {/* Term badge */}
                            {program.term_of && (
                              <span className="px-2 py-0.5 bg-stone-800 text-white text-xs rounded" data-testid={`term-badge-${program.id}`}>
                                {program.term_of}
                              </span>
                            )}
                            {/* Status Badge — Active / Paused / Draft */}
                            {(program.status || 'active') === 'draft' ? (
                              <span
                                className="px-2 py-0.5 text-xs rounded font-medium bg-amber-100 text-amber-900 border border-amber-300"
                                title="Draft — hidden from the public site until you publish it"
                                data-testid={`status-badge-draft-${program.id}`}
                              >
                                Draft — not live
                              </span>
                            ) : (program.status || 'active') === 'paused' ? (
                              <span
                                className="px-2 py-0.5 text-xs rounded font-medium bg-amber-100 text-amber-800 border border-amber-300"
                                title={program.paused_at ? `Paused on ${new Date(program.paused_at).toLocaleDateString()}` : 'Paused — hidden from public listings'}
                                data-testid={`status-badge-paused-${program.id}`}
                              >
                                Paused
                              </span>
                            ) : (
                              <span
                                className="px-2 py-0.5 text-xs rounded font-medium bg-emerald-100 text-emerald-800 border border-emerald-300"
                                title="Active — visible on public listings"
                                data-testid={`status-badge-active-${program.id}`}
                              >
                                Active
                              </span>
                            )}
                            {/* Class Type Badge — display only */}
                            <span className={`px-2 py-0.5 text-xs rounded font-medium ${
                              program.class_type === 'enrollment_managed' 
                                ? 'bg-emerald-100 text-emerald-800 border border-emerald-300' 
                                : program.class_type === 'space_rental'
                                ? 'bg-blue-100 text-blue-800 border border-blue-300'
                                : 'bg-gray-100 text-gray-600 border border-gray-300'
                            }`} title="Class type — display only">
                              {program.class_type === 'enrollment_managed' ? 'Enrollment' : 
                               program.class_type === 'space_rental' ? 'Rental' : 'Informational'}
                            </span>
                            {/* Capacity Warning Badges — admin only, display only */}
                            {(() => {
                              const enrolled = getEnrollmentCount('program', program.id);
                              const cap = parseInt(program.capacity) || 0;
                              if (cap > 0 && enrolled > cap) return (
                                <span className="px-1.5 py-0.5 text-xs font-semibold bg-red-100 text-red-700 rounded border border-red-300"
                                  title={`${enrolled} enrolled exceeds capacity of ${cap}`} data-testid="badge-over-capacity">
                                  Over Capacity
                                </span>
                              );
                              if (cap > 100) return (
                                <span className="px-1.5 py-0.5 text-xs font-semibold bg-amber-100 text-amber-700 rounded border border-amber-300"
                                  title={`Capacity is set to ${cap} — verify this is correct`} data-testid="badge-check-capacity">
                                  Check Capacity
                                </span>
                              );
                              return null;
                            })()}
                          </div>
                          <p className="text-sm text-stone-600 mb-2">{(() => {
                            const raw = program.description || '';
                            const text = raw
                              .replace(/<[^>]*>/g, ' ')
                              .replace(/&nbsp;/g, ' ')
                              .replace(/&amp;/g, '&')
                              .replace(/&lt;/g, '<')
                              .replace(/&gt;/g, '>')
                              .replace(/&quot;/g, '"')
                              .replace(/&#39;/g, "'")
                              .replace(/\s+/g, ' ')
                              .trim();
                            return text.length > 150 ? text.slice(0, 150) + '…' : text;
                          })()}</p>
                          <div className="flex flex-wrap gap-4 text-sm text-stone-500">
                            <span><strong>Instructor:</strong> {program.instructor}</span>
                            <span><strong>Schedule:</strong> {isPrivateLesson(program) ? 'By appointment' : program.schedule}</span>
                            <span><strong>Price:</strong> ${program.price || program.memberPrice || 'N/A'}</span>
                            {program.class_type === 'enrollment_managed' && (
                              <span className="text-emerald-600"><strong>Enrolled:</strong> {getEnrollmentCount('program', program.id)}</span>
                            )}
                            {program.capacity && (
                              <span className="text-blue-600"><strong>Capacity:</strong> {program.capacity}</span>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col gap-1 items-end flex-shrink-0">
                          {(program.class_type === 'enrollment_managed' || program.payment_method === 'paypal') && (
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                              onClick={() => handleManageEnrollment(program)}
                              aria-label={`View roster for ${program.title}`}
                              title="View roster / manage enrollment"
                              data-testid={`roster-btn-${program.id}`}
                            >
                              <ClipboardList size={14} className="mr-1" />
                              Roster
                            </Button>
                          )}
                          {(program.class_type === 'enrollment_managed' || program.payment_method === 'paypal') && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setShareRoster(program)}
                              aria-label={`Share roster with teacher for ${program.title}`}
                              title="Share roster with teacher — creates a private link"
                              data-testid={`share-roster-btn-${program.id}`}
                            >
                              <Share2 size={14} className="mr-1" />
                              Share Roster
                            </Button>
                          )}
                          <div className="flex gap-1 items-center">
                            {(program.status || 'active') === 'draft' && (
                              <Button
                                size="sm"
                                className="bg-emerald-700 hover:bg-emerald-800 text-white mr-1"
                                disabled={publishing === program.id}
                                onClick={() => handlePublishProgram(program)}
                                aria-label={`Publish program ${program.title}`}
                                title="Publish — makes this class visible and bookable on the website"
                                data-testid={`publish-btn-${program.id}`}
                              >
                                <Rocket size={14} className="mr-1" />
                                {publishing === program.id ? 'Publishing…' : 'Publish'}
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-cafa-red hover:bg-cafa-red-50"
                              aria-label={`Copy ${program.title} to the next term`}
                              title="Copy to next term"
                              onClick={() => setNextTerm({ singleProgram: program })}
                              data-testid={`copy-term-btn-${program.id}`}
                            >
                              <CalendarPlus size={16} />
                            </Button>
                            <Button variant="ghost" size="sm" aria-label={`Edit program ${program.title}`} title="Edit program" onClick={() => handleEdit(program, 'program')}>
                              <Edit2 size={16} />
                            </Button>
                            {/* Pause / Reopen — non-destructive, separated visually from Delete */}
                            {(program.status || 'active') === 'paused' ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-emerald-700 hover:bg-emerald-50"
                                aria-label={`Reopen program ${program.title}`}
                                title="Reopen program"
                                onClick={() => setPauseConfirm({ program, action: 'reopen' })}
                                data-testid={`reopen-btn-${program.id}`}
                              >
                                <Play size={16} />
                              </Button>
                            ) : (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-amber-700 hover:bg-amber-50"
                                aria-label={`Pause program ${program.title}`}
                                title="Pause program"
                                onClick={() => setPauseConfirm({ program, action: 'pause' })}
                                data-testid={`pause-btn-${program.id}`}
                              >
                                <Pause size={16} />
                              </Button>
                            )}
                            <span className="mx-1 h-5 w-px bg-stone-300" aria-hidden="true" />
                            <Button variant="ghost" size="sm" className="text-red-600" aria-label={`Delete program ${program.title}`} title="Delete program" onClick={() => handleDelete('program', program.id, program.title)}>
                              <Trash2 size={16} />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* INSTRUCTORS TAB */}
            {activeTab === 'instructors' && (
              <>
                <div className="mb-3 text-xs text-stone-500 flex items-center gap-2" data-testid="instructor-reorder-hint">
                  <GripVertical size={14} className="text-stone-400" />
                  Drag the handle, or use the ▲ / ▼ buttons, to reorder how instructors appear on the public page.
                  {instructorReorderSaving && <span className="text-cafa-red ml-2">Saving…</span>}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {instructors.map((instructor, index) => (
                    <Card
                      key={instructor.id}
                      className={`border-stone-200 transition-opacity ${draggedInstructorIndex === index ? 'opacity-50' : ''}`}
                      draggable
                      onDragStart={() => handleInstructorDragStart(index)}
                      onDragOver={handleInstructorDragOver}
                      onDrop={() => handleInstructorDrop(index)}
                      data-testid={`instructor-card-admin-${instructor.id}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <div
                            className="flex flex-col items-center pt-1 text-stone-400 cursor-grab active:cursor-grabbing"
                            title="Drag to reorder"
                            aria-label={`Drag handle for ${instructor.name}`}
                            data-testid={`instructor-drag-handle-${instructor.id}`}
                          >
                            <GripVertical size={18} />
                          </div>
                          {instructor.imageUrl ? (
                            <img src={mediaUrl(instructor.imageUrl, 'thumb')} alt={instructor.name} className="w-16 h-16 rounded-full object-cover" />
                          ) : (
                            <div className="w-16 h-16 rounded-full bg-stone-200 flex items-center justify-center text-stone-500">
                              <Users size={24} />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-stone-900 truncate">{instructor.name}</h3>
                            <p className="text-sm text-cafa-red mb-1">{instructor.specialty}</p>
                            <p className="text-xs text-stone-500">{instructor.bio?.slice(0, 80)}...</p>
                          </div>
                          <div className="flex flex-col gap-1">
                            <div className="flex gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0"
                                disabled={index === 0 || instructorReorderSaving}
                                onClick={() => moveInstructor(index, -1)}
                                aria-label={`Move ${instructor.name} up`}
                                title="Move up"
                                data-testid={`instructor-move-up-${instructor.id}`}
                              >
                                <ChevronUp size={16} />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0"
                                disabled={index === instructors.length - 1 || instructorReorderSaving}
                                onClick={() => moveInstructor(index, 1)}
                                aria-label={`Move ${instructor.name} down`}
                                title="Move down"
                                data-testid={`instructor-move-down-${instructor.id}`}
                              >
                                <ChevronDown size={16} />
                              </Button>
                            </div>
                            <div className="flex gap-1 border-t border-stone-100 pt-1">
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" aria-label={`Edit instructor ${instructor.name}`} title="Edit instructor" onClick={() => handleEdit(instructor, 'instructor')}>
                                <Edit2 size={16} />
                              </Button>
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-600" aria-label={`Delete instructor ${instructor.name}`} title="Delete instructor" onClick={() => handleDelete('instructor', instructor.id, instructor.name)}>
                                <Trash2 size={16} />
                              </Button>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </>
            )}

            {/* ENROLLMENTS TAB */}
            {activeTab === 'enrollments' && (
              <Card className="border-stone-200">
                <CardHeader className="border-b border-stone-200">
                  <CardTitle className="text-lg font-serif">Enrollment Tracking</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  {enrollments.length === 0 ? (
                    <div className="p-8 text-center text-stone-500">
                      <Calendar size={48} className="mx-auto mb-4 text-stone-300" />
                      <p>No enrollments yet</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-stone-50 border-b border-stone-200">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase">Program/Workshop</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase">Type</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase">Enrolled</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase">Revenue</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase">Attendees</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-stone-200">
                          {enrollments.map(enrollment => (
                            <tr key={enrollment.id} className="hover:bg-stone-50">
                              <td className="px-4 py-3 font-medium text-stone-900">{enrollment.name}</td>
                              <td className="px-4 py-3">
                                <span className={`px-2 py-1 text-xs rounded ${
                                  enrollment.type === 'workshop' ? 'bg-purple-100 text-purple-800' :
                                  enrollment.type === 'membership' ? 'bg-blue-100 text-blue-800' :
                                  'bg-green-100 text-green-800'
                                }`}>
                                  {enrollment.type}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-lg font-bold text-emerald-600">{enrollment.count}</td>
                              <td className="px-4 py-3 text-stone-600">${enrollment.total_revenue.toFixed(2)}</td>
                              <td className="px-4 py-3">
                                <div className="flex flex-wrap gap-1">
                                  {enrollment.attendees.slice(0, 3).map((att, idx) => (
                                    <span key={idx} className="px-2 py-0.5 bg-stone-100 text-stone-700 text-xs rounded" title={att.email}>
                                      {att.name?.split(' ')[0] || 'N/A'}
                                    </span>
                                  ))}
                                  {enrollment.attendees.length > 3 && (
                                    <span className="px-2 py-0.5 bg-stone-200 text-stone-600 text-xs rounded">
                                      +{enrollment.attendees.length - 3}
                                    </span>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>

      {/* Edit Modal */}
      {editingItem && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Edit {editingItem.type}</h2>
              <Button variant="ghost" size="sm" onClick={() => setEditingItem(null)}>
                <X size={18} />
              </Button>
            </div>
            
            <div className="space-y-4">
              {editingItem.type === 'program' && (
                <>
                  <div>
                    <Label>Title</Label>
                    <Input value={editForm.title || ''} onChange={(e) => setEditForm({...editForm, title: e.target.value})} className="mt-1" />
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label>Category</Label>
                      <select value={editForm.category || ''} onChange={(e) => setEditForm({...editForm, category: e.target.value})} className="mt-1 w-full px-3 py-2 border border-stone-300 rounded-md">
                        <option value="Painting">Painting</option>
                        <option value="Drawing">Drawing</option>
                        <option value="Youth">Youth</option>
                        <option value="Open Studio">Open Studio</option>
                      </select>
                    </div>
                    <div>
                      <Label>Level</Label>
                      <Input value={editForm.level || ''} onChange={(e) => setEditForm({...editForm, level: e.target.value})} className="mt-1" />
                    </div>
                    <div>
                      <Label>Class Type</Label>
                      <select 
                        value={editForm.class_type || 'informational'} 
                        onChange={(e) => setEditForm({...editForm, class_type: e.target.value})} 
                        className="mt-1 w-full px-3 py-2 border border-stone-300 rounded-md"
                      >
                        <option value="informational">Informational (Content Only)</option>
                        <option value="enrollment_managed">Enrollment Managed (Registration/Payments)</option>
                        <option value="space_rental">Space Rental</option>
                      </select>
                    </div>
                  </div>
                  {/* Show capacity field only for enrollment_managed classes */}
                  {editForm.class_type === 'enrollment_managed' && (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-md p-4">
                      <Label className="text-emerald-800 font-medium">Enrollment Settings</Label>
                      <p className="text-xs text-emerald-600 mb-3">This class supports registration and payment tracking.</p>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Max Capacity</Label>
                          <Input 
                            type="number" 
                            value={editForm.capacity || ''} 
                            onChange={(e) => setEditForm({...editForm, capacity: e.target.value ? parseInt(e.target.value) : null})} 
                            className="mt-1" 
                            placeholder="Leave empty for unlimited"
                          />
                        </div>
                        <div>
                          <Label>Class Image URL</Label>
                          <Input 
                            value={editForm.classImage || editForm.instructorImage || ''} 
                            onChange={(e) => setEditForm({...editForm, classImage: e.target.value})} 
                            className="mt-1" 
                            placeholder="Image URL for class card (class photo, not instructor headshot)"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                  <div>
                    <Label>Description</Label>
                    <Textarea value={editForm.description || ''} onChange={(e) => setEditForm({...editForm, description: e.target.value})} className="mt-1" rows={3} />
                  </div>
                  <div>
                    <Label>Instructor</Label>
                    <Input value={editForm.instructor || ''} onChange={(e) => setEditForm({...editForm, instructor: e.target.value})} className="mt-1" />
                  </div>
                  {/* Payment Method */}
                  <div className="border border-stone-200 rounded-md p-4 bg-stone-50">
                    <Label className="font-medium text-stone-900 block mb-3">Payment Method</Label>
                    <div className="space-y-3">
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="radio"
                          name="edit_payment_method"
                          value="instructor"
                          checked={(editForm.payment_method || 'instructor') === 'instructor'}
                          onChange={() => setEditForm({...editForm, payment_method: 'instructor'})}
                          className="mt-0.5 accent-cafa-red"
                        />
                        <div>
                          <span className="font-medium text-stone-800 text-sm">Instructor takes payment</span>
                          <p className="text-xs text-stone-500">Students contact the instructor directly to arrange payment</p>
                        </div>
                      </label>
                      {(editForm.payment_method || 'instructor') === 'instructor' && (
                        <div className="ml-6">
                          <Label>Instructor Email</Label>
                          <Input value={editForm.instructorEmail || ''} onChange={(e) => setEditForm({...editForm, instructorEmail: e.target.value})} className="mt-1" placeholder="instructor@email.com" />
                        </div>
                      )}
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="radio"
                          name="edit_payment_method"
                          value="paypal"
                          checked={editForm.payment_method === 'paypal'}
                          onChange={() => setEditForm({...editForm, payment_method: 'paypal'})}
                          className="mt-0.5 accent-cafa-red"
                        />
                        <div>
                          <span className="font-medium text-stone-800 text-sm">CAFA takes payment via PayPal</span>
                          <p className="text-xs text-stone-500">Students pay CAFA directly through a PayPal link</p>
                        </div>
                      </label>
                      {editForm.payment_method === 'paypal' && (
                        <div className="ml-6 p-3 bg-blue-50 border border-blue-200 rounded-md">
                          <p className="text-sm text-blue-800 font-medium">Uses site's PayPal Orders API</p>
                          <p className="text-xs text-blue-600 mt-1">Students will complete a checkout page with PayPal Smart Buttons (credit card &amp; PayPal wallet). The class price will be charged automatically.</p>
                        </div>
                      )}
                    </div>
                  </div>
                  <ProgramListingSettings
                    value={editForm}
                    onPatch={(patch) => setEditForm({ ...editForm, ...patch })}
                    idPrefix="prog_edit"
                  />
                  {editForm.isPayPal !== true && (
                    <PrivateLessonSettings
                      value={editForm}
                      onPatch={(patch) => setEditForm({ ...editForm, ...patch })}
                      idPrefix="prog_edit"
                    />
                  )}
                  {!isPrivateLesson(editForm) && (
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label>Price ($)</Label>
                      <Input type="number" value={editForm.price || 0} onChange={(e) => setEditForm({...editForm, price: parseFloat(e.target.value)})} className="mt-1" />
                    </div>
                    <div>
                      <Label>Duration</Label>
                      <Input value={editForm.duration || ''} onChange={(e) => setEditForm({...editForm, duration: e.target.value})} className="mt-1" />
                    </div>
                    <div>
                      <Label>Schedule</Label>
                      <Input value={editForm.schedule || ''} onChange={(e) => setEditForm({...editForm, schedule: e.target.value})} className="mt-1" />
                    </div>
                  </div>
                  )}
                  {/* Class Image — deduplicating media picker */}
                  <div>
                    <Label>Class Image</Label>
                    <p className="text-xs text-stone-500 mt-0.5 mb-1.5">A photo of the class or studio shown on the class card. Use the Instructors tab for instructor headshots.</p>
                    <MediaPicker
                      token={token}
                      value={editForm.classImage || editForm.instructorImage || ''}
                      imageRef={editForm.image_ref}
                      purpose="program"
                      idPrefix="prog-edit"
                      onChange={({ url, image_ref }) => setEditForm(prev => ({
                        ...prev, classImage: url, image_ref: image_ref || null,
                      }))}
                    />
                  </div>
                </>
              )}

              {editingItem.type === 'workshop' && (
                <>
                  <div>
                    <Label>Title</Label>
                    <Input value={editForm.title || ''} onChange={(e) => setEditForm({...editForm, title: e.target.value})} className="mt-1" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Instructor</Label>
                      <Input value={editForm.instructor || ''} onChange={(e) => setEditForm({...editForm, instructor: e.target.value})} className="mt-1" />
                    </div>
                    <div>
                      <Label>Level</Label>
                      <Input value={editForm.level || ''} onChange={(e) => setEditForm({...editForm, level: e.target.value})} className="mt-1" />
                    </div>
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Textarea value={editForm.description || ''} onChange={(e) => setEditForm({...editForm, description: e.target.value})} className="mt-1" rows={3} />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Start Date (YYYY-MM-DD)</Label>
                      <Input value={editForm.date || ''} onChange={(e) => setEditForm({...editForm, date: e.target.value})} className="mt-1" />
                    </div>
                    <div>
                      <Label>End Date (YYYY-MM-DD)</Label>
                      <Input value={editForm.endDate || ''} onChange={(e) => setEditForm({...editForm, endDate: e.target.value})} className="mt-1" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Schedule Text</Label>
                      <Input value={editForm.schedule || ''} onChange={(e) => setEditForm({...editForm, schedule: e.target.value})} className="mt-1" />
                    </div>
                    <div>
                      <Label>Time</Label>
                      <Input value={editForm.time || ''} onChange={(e) => setEditForm({...editForm, time: e.target.value})} className="mt-1" />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label>Member Price ($)</Label>
                      <Input type="number" value={editForm.memberPrice || 0} onChange={(e) => setEditForm({...editForm, memberPrice: parseFloat(e.target.value)})} className="mt-1" />
                    </div>
                    <div>
                      <Label>Non-Member Price ($)</Label>
                      <Input type="number" value={editForm.nonMemberPrice || 0} onChange={(e) => setEditForm({...editForm, nonMemberPrice: parseFloat(e.target.value)})} className="mt-1" />
                    </div>
                    <div>
                      <Label>Base Price ($)</Label>
                      <Input type="number" value={editForm.price || 0} onChange={(e) => setEditForm({...editForm, price: parseFloat(e.target.value)})} className="mt-1" />
                    </div>
                  </div>
                  <div>
                    <Label>Materials</Label>
                    <Input value={editForm.materials || ''} onChange={(e) => setEditForm({...editForm, materials: e.target.value})} className="mt-1" />
                  </div>
                  <div>
                    <Label>Instructor Image URL</Label>
                    <Input value={editForm.instructorImage || ''} onChange={(e) => setEditForm({...editForm, instructorImage: e.target.value})} className="mt-1" />
                  </div>
                </>
              )}

              {editingItem.type === 'instructor' && (
                <>
                  <div>
                    <Label>Name</Label>
                    <Input value={editForm.name || ''} onChange={(e) => setEditForm({...editForm, name: e.target.value})} className="mt-1" />
                  </div>
                  <div>
                    <Label>Specialty</Label>
                    <Input value={editForm.specialty || ''} onChange={(e) => setEditForm({...editForm, specialty: e.target.value})} className="mt-1" />
                  </div>
                  <div>
                    <Label>Bio</Label>
                    <Textarea value={editForm.bio || ''} onChange={(e) => setEditForm({...editForm, bio: e.target.value})} className="mt-1" rows={3} />
                  </div>
                  <div>
                    <Label>Image URL</Label>
                    <Input value={editForm.imageUrl || ''} onChange={(e) => setEditForm({...editForm, imageUrl: e.target.value})} className="mt-1" />
                  </div>
                </>
              )}
              
              <div className="flex items-center gap-4 pt-4">
                <Button onClick={handleSave} disabled={saving} className="flex-1 bg-cafa-red hover:bg-cafa-red-900 text-white">
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
                <Button variant="outline" onClick={() => setEditingItem(null)}>
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Add New {activeTab.slice(0, -1)}</h2>
              <Button variant="ghost" size="sm" onClick={() => setShowAddModal(false)}>
                <X size={18} />
              </Button>
            </div>
            
            <div className="space-y-4">
              {activeTab === 'programs' && (
                <>
                  <div>
                    <Label>Title *</Label>
                    <Input value={editForm.title || ''} onChange={(e) => setEditForm({...editForm, title: e.target.value})} className="mt-1" />
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label>Category</Label>
                      <select value={editForm.category || 'Painting'} onChange={(e) => setEditForm({...editForm, category: e.target.value})} className="mt-1 w-full px-3 py-2 border border-stone-300 rounded-md">
                        <option value="Painting">Painting</option>
                        <option value="Drawing">Drawing</option>
                        <option value="Youth">Youth</option>
                        <option value="Open Studio">Open Studio</option>
                      </select>
                    </div>
                    <div>
                      <Label>Level</Label>
                      <Input value={editForm.level || ''} onChange={(e) => setEditForm({...editForm, level: e.target.value})} className="mt-1" placeholder="e.g., All Levels, Beginner" />
                    </div>
                    <div>
                      <Label>Class Type</Label>
                      <select 
                        value={editForm.class_type || 'informational'} 
                        onChange={(e) => setEditForm({...editForm, class_type: e.target.value})} 
                        className="mt-1 w-full px-3 py-2 border border-stone-300 rounded-md"
                      >
                        <option value="informational">Informational</option>
                        <option value="enrollment_managed">Enrollment Managed</option>
                        <option value="space_rental">Space Rental</option>
                      </select>
                    </div>
                  </div>
                  {/* Show capacity field only for enrollment_managed classes */}
                  {editForm.class_type === 'enrollment_managed' && (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-md p-4">
                      <Label className="text-emerald-800 font-medium">Enrollment Settings</Label>
                      <p className="text-xs text-emerald-600 mb-3">This class supports registration and payment tracking.</p>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Max Capacity</Label>
                          <Input 
                            type="number" 
                            value={editForm.capacity || ''} 
                            onChange={(e) => setEditForm({...editForm, capacity: e.target.value ? parseInt(e.target.value) : null})} 
                            className="mt-1" 
                            placeholder="Leave empty for unlimited"
                          />
                        </div>
                        <div>
                          <Label>Class Image URL</Label>
                          <Input 
                            value={editForm.classImage || editForm.instructorImage || ''} 
                            onChange={(e) => setEditForm({...editForm, classImage: e.target.value})} 
                            className="mt-1" 
                            placeholder="Image URL for class card (class photo, not instructor headshot)"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                  <div>
                    <Label>Description</Label>
                    <Textarea value={editForm.description || ''} onChange={(e) => setEditForm({...editForm, description: e.target.value})} className="mt-1" rows={3} />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Instructor</Label>
                      <Input value={editForm.instructor || ''} onChange={(e) => setEditForm({...editForm, instructor: e.target.value})} className="mt-1" />
                    </div>
                    {!isPrivateLesson(editForm) && editForm.isPayPal !== true && (
                    <div>
                      <Label>Price ($)</Label>
                      <Input type="number" value={editForm.price || 0} onChange={(e) => setEditForm({...editForm, price: parseFloat(e.target.value)})} className="mt-1" />
                    </div>
                    )}
                  </div>
                  {/* Payment Method */}
                  <div className="border border-stone-200 rounded-md p-4 bg-stone-50">
                    <Label className="font-medium text-stone-900 block mb-3">Payment Method</Label>
                    <div className="space-y-3">
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="radio"
                          name="add_payment_method"
                          value="instructor"
                          checked={(editForm.payment_method || 'instructor') === 'instructor'}
                          onChange={() => setEditForm({...editForm, payment_method: 'instructor'})}
                          className="mt-0.5 accent-cafa-red"
                        />
                        <div>
                          <span className="font-medium text-stone-800 text-sm">Instructor takes payment</span>
                          <p className="text-xs text-stone-500">Students contact the instructor directly to arrange payment</p>
                        </div>
                      </label>
                      {(editForm.payment_method || 'instructor') === 'instructor' && (
                        <div className="ml-6">
                          <Label>Instructor Email</Label>
                          <Input value={editForm.instructorEmail || ''} onChange={(e) => setEditForm({...editForm, instructorEmail: e.target.value})} className="mt-1" placeholder="instructor@email.com" />
                        </div>
                      )}
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="radio"
                          name="add_payment_method"
                          value="paypal"
                          checked={editForm.payment_method === 'paypal'}
                          onChange={() => setEditForm({...editForm, payment_method: 'paypal'})}
                          className="mt-0.5 accent-cafa-red"
                        />
                        <div>
                          <span className="font-medium text-stone-800 text-sm">CAFA takes payment via PayPal</span>
                          <p className="text-xs text-stone-500">Students pay CAFA directly through a PayPal link</p>
                        </div>
                      </label>
                      {editForm.payment_method === 'paypal' && (
                        <div className="ml-6 p-3 bg-blue-50 border border-blue-200 rounded-md">
                          <p className="text-sm text-blue-800 font-medium">Uses site's PayPal Orders API</p>
                          <p className="text-xs text-blue-600 mt-1">Students will complete a checkout page with PayPal Smart Buttons (credit card &amp; PayPal wallet). The class price will be charged automatically.</p>
                        </div>
                      )}
                    </div>
                  </div>
                  <ProgramListingSettings
                    value={editForm}
                    onPatch={(patch) => setEditForm({ ...editForm, ...patch })}
                    idPrefix="prog_add"
                  />
                  {editForm.isPayPal !== true && (
                    <PrivateLessonSettings
                      value={editForm}
                      onPatch={(patch) => setEditForm({ ...editForm, ...patch })}
                      idPrefix="prog_add"
                    />
                  )}
                  {!isPrivateLesson(editForm) && (
                  <div>
                    <Label>Schedule</Label>
                    <Input value={editForm.schedule || ''} onChange={(e) => setEditForm({...editForm, schedule: e.target.value})} className="mt-1" placeholder="e.g., Mondays, 9 AM - 12 PM" />
                  </div>
                  )}
                  {/* Program Photo — deduplicating media picker */}
                  <div>
                    <Label>Class Image</Label>
                    <p className="text-xs text-stone-500 mt-0.5 mb-1.5">A photo of the class or studio shown on the class card. Use the Instructors tab for instructor headshots.</p>
                    <MediaPicker
                      token={token}
                      value={editForm.classImage || editForm.instructorImage || ''}
                      imageRef={editForm.image_ref}
                      purpose="program"
                      idPrefix="prog-add"
                      onChange={({ url, image_ref }) => setEditForm(prev => ({
                        ...prev, classImage: url, image_ref: image_ref || null,
                      }))}
                    />
                  </div>
                </>
              )}

              {activeTab === 'workshops' && (
                <>
                  <div>
                    <Label>Title *</Label>
                    <Input value={editForm.title || ''} onChange={(e) => setEditForm({...editForm, title: e.target.value})} className="mt-1" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Instructor</Label>
                      <Input value={editForm.instructor || ''} onChange={(e) => setEditForm({...editForm, instructor: e.target.value})} className="mt-1" />
                    </div>
                    <div>
                      <Label>Level</Label>
                      <Input value={editForm.level || ''} onChange={(e) => setEditForm({...editForm, level: e.target.value})} className="mt-1" />
                    </div>
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Textarea value={editForm.description || ''} onChange={(e) => setEditForm({...editForm, description: e.target.value})} className="mt-1" rows={3} />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Start Date (YYYY-MM-DD)</Label>
                      <Input value={editForm.date || ''} onChange={(e) => setEditForm({...editForm, date: e.target.value})} className="mt-1" />
                    </div>
                    <div>
                      <Label>End Date (YYYY-MM-DD)</Label>
                      <Input value={editForm.endDate || ''} onChange={(e) => setEditForm({...editForm, endDate: e.target.value})} className="mt-1" />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label>Member Price ($)</Label>
                      <Input type="number" value={editForm.memberPrice || 0} onChange={(e) => setEditForm({...editForm, memberPrice: parseFloat(e.target.value)})} className="mt-1" />
                    </div>
                    <div>
                      <Label>Non-Member Price ($)</Label>
                      <Input type="number" value={editForm.nonMemberPrice || 0} onChange={(e) => setEditForm({...editForm, nonMemberPrice: parseFloat(e.target.value)})} className="mt-1" />
                    </div>
                    <div>
                      <Label>Time</Label>
                      <Input value={editForm.time || ''} onChange={(e) => setEditForm({...editForm, time: e.target.value})} className="mt-1" placeholder="e.g., 9 AM - 4 PM" />
                    </div>
                  </div>
                </>
              )}

              {activeTab === 'instructors' && (
                <>
                  <div>
                    <Label>Name *</Label>
                    <Input value={editForm.name || ''} onChange={(e) => setEditForm({...editForm, name: e.target.value})} className="mt-1" />
                  </div>
                  <div>
                    <Label>Specialty</Label>
                    <Input value={editForm.specialty || ''} onChange={(e) => setEditForm({...editForm, specialty: e.target.value})} className="mt-1" placeholder="e.g., Oil Painting, Figure Drawing" />
                  </div>
                  <div>
                    <Label>Bio</Label>
                    <Textarea value={editForm.bio || ''} onChange={(e) => setEditForm({...editForm, bio: e.target.value})} className="mt-1" rows={3} />
                  </div>
                  <div>
                    <Label>Image URL</Label>
                    <Input value={editForm.imageUrl || ''} onChange={(e) => setEditForm({...editForm, imageUrl: e.target.value})} className="mt-1" />
                  </div>
                </>
              )}
              
              <div className="flex items-center gap-4 pt-4">
                <Button onClick={handleAdd} disabled={saving} className="flex-1 bg-cafa-red hover:bg-cafa-red-900 text-white">
                  {saving ? 'Creating...' : 'Create'}
                </Button>
                <Button variant="outline" onClick={() => setShowAddModal(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Enrollment Management Modal */}
      {enrollmentModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto"
          role="dialog"
          aria-modal="true"
          aria-label="Manage program enrollment"
          data-testid="program-roster-modal"
        >
          <div className="bg-white rounded-lg max-w-4xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold text-stone-900">Manage Enrollment</h2>
                <p className="text-sm text-stone-600">{enrollmentModal.title}</p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="border-stone-300 text-stone-700 hover:bg-stone-50"
                  onClick={handlePrintRoster}
                  title="Print attendance roster"
                >
                  Print Roster
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setEnrollmentModal(null)}>
                  <X size={18} />
                </Button>
              </div>
            </div>
            
            {enrollmentLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cafa-red"></div>
              </div>
            ) : enrollmentData ? (
              <div className="space-y-6">
                {/* Roster vs Card Count Mismatch Warning (read-only) */}
                {(() => {
                  const cardCount = getEnrollmentCount('program', enrollmentModal?.id);
                  const rosterActive = (enrollmentData.registrations || []).filter(r => r.status !== 'removed').length;
                  const enrolledStat = enrollmentData.enrolled ?? rosterActive;
                  const mismatch = (cardCount !== enrolledStat) || (rosterActive !== enrolledStat);
                  if (!mismatch) return null;
                  return (
                    <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 flex items-start gap-2" data-testid="enrollment-mismatch-warning">
                      <span className="text-amber-700 text-lg leading-none">⚠️</span>
                      <div className="text-sm text-amber-900">
                        <div className="font-semibold">Enrollment count mismatch detected</div>
                        <div className="text-xs mt-1">
                          Card shows <strong>{cardCount}</strong> enrolled, this dashboard shows <strong>{enrolledStat}</strong>, and the active roster has <strong>{rosterActive}</strong> entries.
                          This is informational only — no action has been taken. Use "Refresh" or investigate manually before reconciling.
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* Enrollment Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-200">
                    <p className="text-xs text-emerald-600 uppercase font-medium">Enrolled</p>
                    <p className="text-2xl font-bold text-emerald-700">{enrollmentData.enrolled}</p>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                    <p className="text-xs text-blue-600 uppercase font-medium">Capacity</p>
                    <p className="text-2xl font-bold text-blue-700">{enrollmentData.capacity || '∞'}</p>
                  </div>
                  <div className="bg-stone-50 rounded-lg p-4 border border-stone-200">
                    <p className="text-xs text-stone-600 uppercase font-medium">Available</p>
                    <p className="text-2xl font-bold text-stone-700">
                      {enrollmentData.spots_available !== null ? enrollmentData.spots_available : '∞'}
                    </p>
                  </div>
                  <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                    <p className="text-xs text-amber-600 uppercase font-medium">Revenue</p>
                    <p className="text-2xl font-bold text-amber-700">${enrollmentData.total_revenue?.toFixed(2) || '0.00'}</p>
                  </div>
                </div>

                {/* Add Student Form */}
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                  <h3 className="font-medium text-emerald-800 mb-3">Add Student Manually</h3>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <Input 
                      placeholder="Student Name *" 
                      value={addStudentForm.student_name}
                      onChange={(e) => setAddStudentForm({...addStudentForm, student_name: e.target.value})}
                    />
                    <Input 
                      placeholder="Email *" 
                      type="email"
                      value={addStudentForm.student_email}
                      onChange={(e) => setAddStudentForm({...addStudentForm, student_email: e.target.value})}
                    />
                    <Input 
                      placeholder="Amount Paid" 
                      type="number"
                      value={addStudentForm.amount_paid}
                      onChange={(e) => setAddStudentForm({...addStudentForm, amount_paid: parseFloat(e.target.value) || 0})}
                    />
                    <Button 
                      onClick={handleAddStudent} 
                      disabled={saving}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    >
                      {saving ? 'Adding...' : 'Add Student'}
                    </Button>
                  </div>
                </div>

                {/* Term Selector */}
                <div className="flex items-center gap-2 mt-4 mb-2 flex-wrap" data-testid="roster-term-selector">
                  <span className="text-xs font-medium text-stone-600 inline-flex items-center gap-1"><History size={12} /> Roster:</span>
                  <button
                    onClick={() => { setRosterTerm('current'); handleManageEnrollment(enrollmentModal, 'current'); }}
                    className={`text-xs px-2 py-1 rounded border ${rosterTerm === 'current' ? 'bg-stone-900 text-white border-stone-900' : 'bg-white text-stone-700 border-stone-300 hover:bg-stone-50'}`}
                  >Current term</button>
                  {termHistory.length > 0 && termHistory.map(t => (
                    <button
                      key={t.term_label}
                      onClick={() => { setRosterTerm(t.term_label); handleManageEnrollment(enrollmentModal, t.term_label); }}
                      className={`text-xs px-2 py-1 rounded border ${rosterTerm === t.term_label ? 'bg-stone-900 text-white border-stone-900' : 'bg-white text-stone-700 border-stone-300 hover:bg-stone-50'}`}
                      title={t.archived_at ? `Archived ${new Date(t.archived_at).toLocaleDateString()}` : ''}
                    >
                      {t.term_label} ({t.count})
                    </button>
                  ))}
                  <button
                    onClick={() => { setRosterTerm('all'); handleManageEnrollment(enrollmentModal, 'all'); }}
                    className={`text-xs px-2 py-1 rounded border ${rosterTerm === 'all' ? 'bg-stone-900 text-white border-stone-900' : 'bg-white text-stone-700 border-stone-300 hover:bg-stone-50'}`}
                  >All</button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="ml-auto border-amber-300 text-amber-800 hover:bg-amber-50 h-7 text-xs"
                    onClick={openNewTermModal}
                    data-testid="start-new-term-btn"
                  >
                    <RotateCcw size={14} className="mr-1" /> Start New Term
                  </Button>
                </div>

                {/* Roster Table */}
                <div className="border border-stone-200 rounded-lg overflow-hidden">
                  <div className="bg-stone-50 px-4 py-3 border-b border-stone-200">
                    <h3 className="font-medium text-stone-800">Class Roster ({enrollmentData.registrations?.length || 0})</h3>
                  </div>
                  {enrollmentData.registrations?.length > 0 ? (
                    <table className="w-full">
                      <thead className="bg-stone-100 text-xs uppercase text-stone-600">
                        <tr>
                          <th className="px-4 py-2 text-left">Student</th>
                          <th className="px-4 py-2 text-left">Email</th>
                          <th className="px-4 py-2 text-left">Phone</th>
                          <th className="px-4 py-2 text-left">Amount</th>
                          <th className="px-4 py-2 text-left">Status</th>
                          <th className="px-4 py-2 text-left">Date</th>
                          <th className="px-4 py-2 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-stone-200">
                        {enrollmentData.registrations.map((reg) => (
                          <tr key={reg.id} className={`hover:bg-stone-50 ${reg.status === 'removed' ? 'bg-red-50 opacity-60' : ''}`}>
                            <td className="px-4 py-3 font-medium text-stone-900">{reg.student_name}</td>
                            <td className="px-4 py-3 text-stone-600">{reg.student_email}</td>
                            <td className="px-4 py-3 text-stone-600" data-testid={`roster-phone-${reg.id}`}>
                              {reg.phone ? (
                                <a href={`tel:${reg.phone}`} className="text-amber-700 hover:underline">{reg.phone}</a>
                              ) : '—'}
                            </td>
                            <td className="px-4 py-3 text-stone-600">${reg.amount_paid?.toFixed(2) || '0.00'}</td>
                            <td className="px-4 py-3">
                              <span className={`px-2 py-1 text-xs rounded ${
                                reg.status === 'completed' ? 'bg-emerald-100 text-emerald-800' :
                                reg.status === 'removed' ? 'bg-red-100 text-red-800' :
                                'bg-stone-100 text-stone-800'
                              }`}>
                                {reg.status || 'active'}
                              </span>
                              {reg.refund_status === 'refunded' && (
                                <span className="ml-1 px-2 py-1 text-xs rounded bg-amber-100 text-amber-800">Refunded</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-stone-500 text-sm">
                              {reg.registered_at ? new Date(reg.registered_at).toLocaleDateString() : '-'}
                            </td>
                            <td className="px-4 py-3 text-right">
                              {!['removed', 'refunded'].includes(reg.status) && !reg.archived_term && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-amber-700 hover:bg-amber-50"
                                  aria-label={`Refund ${reg.student_name}`}
                                  title="Refund / remove from roster"
                                  onClick={() => openRefundModal(reg)}
                                  data-testid={`refund-btn-${reg.id}`}
                                >
                                  <RotateCcw size={14} className="mr-1" /> Refund
                                </Button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div className="p-8 text-center text-stone-500">
                      <Users size={32} className="mx-auto mb-2 text-stone-300" />
                      <p>No students enrolled yet</p>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-center py-12 text-stone-500">
                <p>Unable to load enrollment data</p>
              </div>
            )}
            
            <div className="mt-6 flex justify-end">
              <Button variant="outline" onClick={() => setEnrollmentModal(null)}>
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ── Refund Registration Modal ────────────────────────────────────── */}
      {refundModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="refund-modal">
          <div className="bg-white rounded-lg max-w-lg w-full p-6 shadow-xl">
            <div className="flex items-start gap-3 mb-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                <RotateCcw size={20} className="text-amber-700" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-semibold text-stone-900">Refund this student?</h3>
                <p className="text-sm text-stone-600">
                  <span className="font-medium">{refundModal.student.student_name}</span> — ${refundModal.student.amount_paid || 0} paid
                </p>
              </div>
            </div>

            <div className="space-y-3 mb-4">
              <Label className="text-sm font-medium">Refund mode</Label>
              <div className="space-y-2">
                <label className="flex items-start gap-2 p-3 border rounded cursor-pointer hover:bg-stone-50 has-[:checked]:bg-amber-50 has-[:checked]:border-amber-300">
                  <input
                    type="radio" name="refund-mode" value="paypal_auto"
                    checked={refundForm.mode === 'paypal_auto'}
                    onChange={() => setRefundForm({ ...refundForm, mode: 'paypal_auto' })}
                    disabled={!refundModal.student.paypal_capture_id && !refundModal.student.payment_id}
                    data-testid="refund-mode-paypal"
                  />
                  <div className="text-sm">
                    <div className="font-medium">Auto-refund via PayPal</div>
                    <div className="text-xs text-stone-500">
                      Calls PayPal to issue the refund automatically.
                      {!refundModal.student.paypal_capture_id && !refundModal.student.payment_id && ' (unavailable — no PayPal capture on this registration)'}
                    </div>
                  </div>
                </label>
                <label className="flex items-start gap-2 p-3 border rounded cursor-pointer hover:bg-stone-50 has-[:checked]:bg-amber-50 has-[:checked]:border-amber-300">
                  <input type="radio" name="refund-mode" value="manual"
                    checked={refundForm.mode === 'manual'}
                    onChange={() => setRefundForm({ ...refundForm, mode: 'manual' })}
                    data-testid="refund-mode-manual"
                  />
                  <div className="text-sm">
                    <div className="font-medium">Mark refunded (offline)</div>
                    <div className="text-xs text-stone-500">For cash, check, or already-refunded payments.</div>
                  </div>
                </label>
                <label className="flex items-start gap-2 p-3 border rounded cursor-pointer hover:bg-stone-50 has-[:checked]:bg-amber-50 has-[:checked]:border-amber-300">
                  <input type="radio" name="refund-mode" value="cancel_only"
                    checked={refundForm.mode === 'cancel_only'}
                    onChange={() => setRefundForm({ ...refundForm, mode: 'cancel_only' })}
                    data-testid="refund-mode-cancel"
                  />
                  <div className="text-sm">
                    <div className="font-medium">Cancel only (no refund)</div>
                    <div className="text-xs text-stone-500">Free registration or duplicate sign-up where no money changed hands.</div>
                  </div>
                </label>
              </div>

              <div>
                <Label htmlFor="ac-refund-reason" className="text-sm">Reason (optional, visible in audit log)</Label>
                <input
                  id="ac-refund-reason"
                  value={refundForm.reason}
                  onChange={(e) => setRefundForm({ ...refundForm, reason: e.target.value })}
                  placeholder="e.g. duplicate registration, pre-class dropout"
                  className="mt-1 w-full px-3 py-2 border border-stone-300 rounded text-sm"
                  data-testid="refund-reason-input"
                />
              </div>

              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox"
                  checked={refundForm.send_email}
                  onChange={(e) => setRefundForm({ ...refundForm, send_email: e.target.checked })}
                  data-testid="refund-send-email-toggle"
                />
                Send refund confirmation email to {refundModal.student.student_email || 'student'}
              </label>
            </div>

            <p className="text-xs text-stone-500 mb-4 flex items-start gap-1">
              <AlertTriangle size={12} className="mt-0.5 flex-shrink-0" />
              Non-destructive — the registration record stays in the system for audit. The student no longer counts toward enrollment capacity.
            </p>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setRefundModal(null)} disabled={refundSubmitting}>Cancel</Button>
              <Button onClick={handleRefundSubmit} disabled={refundSubmitting}
                className="bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="refund-confirm-btn"
              >
                {refundSubmitting ? 'Processing…' : 'Confirm Refund'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ── Start New Term Modal ─────────────────────────────────────────── */}
      {newTermModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="new-term-modal">
          <div className="bg-white rounded-lg max-w-lg w-full p-6 shadow-xl">
            <div className="flex items-start gap-3 mb-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                <RotateCcw size={20} className="text-amber-700" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-semibold text-stone-900">Start a new term?</h3>
                <p className="text-sm text-stone-600">{newTermModal.title}</p>
              </div>
            </div>
            <p className="text-sm text-stone-600 mb-4">
              Archives the current roster ({enrollmentData?.enrolled ?? 0} student{(enrollmentData?.enrolled ?? 0) === 1 ? '' : 's'}) under the label below and resets the class for fresh enrollments. Past students remain visible under term history. <strong>Nothing is deleted.</strong>
            </p>
            <div className="space-y-3 mb-4">
              <div>
                <Label className="text-sm">Label the term being closed <span className="text-red-500">*</span></Label>
                <input
                  value={newTermForm.previous_term_label}
                  onChange={(e) => setNewTermForm({ ...newTermForm, previous_term_label: e.target.value })}
                  placeholder='e.g. "Spring 2026"'
                  className="mt-1 w-full px-3 py-2 border border-stone-300 rounded text-sm"
                  data-testid="new-term-prev-label"
                />
              </div>
              <div>
                <Label className="text-sm">New term label (optional)</Label>
                <input
                  value={newTermForm.new_term_label}
                  onChange={(e) => setNewTermForm({ ...newTermForm, new_term_label: e.target.value })}
                  placeholder='e.g. "Summer 2026"'
                  className="mt-1 w-full px-3 py-2 border border-stone-300 rounded text-sm"
                  data-testid="new-term-new-label"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm">New schedule (optional)</Label>
                  <input
                    value={newTermForm.new_schedule}
                    onChange={(e) => setNewTermForm({ ...newTermForm, new_schedule: e.target.value })}
                    placeholder="e.g. Thursdays, 6 PM"
                    className="mt-1 w-full px-3 py-2 border border-stone-300 rounded text-sm"
                  />
                </div>
                <div>
                  <Label className="text-sm">New duration (optional)</Label>
                  <input
                    value={newTermForm.new_duration}
                    onChange={(e) => setNewTermForm({ ...newTermForm, new_duration: e.target.value })}
                    placeholder="e.g. 8 weeks"
                    className="mt-1 w-full px-3 py-2 border border-stone-300 rounded text-sm"
                  />
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setNewTermModal(null)} disabled={newTermSubmitting}>Cancel</Button>
              <Button
                onClick={handleStartNewTerm}
                disabled={newTermSubmitting || !newTermForm.previous_term_label.trim()}
                className="bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="new-term-confirm-btn"
              >
                {newTermSubmitting ? 'Archiving…' : 'Archive & Start New Term'}
              </Button>
            </div>
          </div>
        </div>
      )}



      {/* ── Pause / Reopen Program Confirm Modal ─────────────────────────── */}
      {pauseConfirm && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          data-testid="pause-confirm-modal"
        >
          <div className="bg-white rounded-lg max-w-md w-full p-6 shadow-xl">
            <div className="flex items-start gap-3 mb-4">
              <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${pauseConfirm.action === 'pause' ? 'bg-amber-100' : 'bg-emerald-100'}`}>
                {pauseConfirm.action === 'pause'
                  ? <Pause size={20} className="text-amber-700" />
                  : <Play size={20} className="text-emerald-700" />}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-semibold text-stone-900">
                  {pauseConfirm.action === 'pause' ? 'Pause this program?' : 'Reopen this program?'}
                </h3>
                <p className="text-sm text-stone-600 mt-1">
                  <span className="font-medium">{pauseConfirm.program?.title}</span>
                </p>
              </div>
            </div>
            <p className="text-sm text-stone-600 mb-4">
              {pauseConfirm.action === 'pause'
                ? 'It will be hidden from active public listings but all program details and history (instructor, schedule, image, price, registrations, roster) will be preserved.'
                : 'It will return to active public listings. All previous program data is intact and admins can update the schedule afterward if needed.'}
            </p>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setPauseConfirm(null)}
                disabled={pauseSubmitting}
                data-testid="pause-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={handlePauseProgram}
                disabled={pauseSubmitting}
                className={pauseConfirm.action === 'pause'
                  ? 'bg-amber-600 hover:bg-amber-700 text-white'
                  : 'bg-emerald-600 hover:bg-emerald-700 text-white'}
                data-testid="pause-confirm-btn"
              >
                {pauseSubmitting
                  ? <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />Working…</>
                  : (pauseConfirm.action === 'pause'
                      ? <><Pause size={15} className="mr-1.5" />Pause Program</>
                      : <><Play size={15} className="mr-1.5" />Reopen Program</>)}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Copy to Next Term Modal */}
      <NextTermModal
        open={!!nextTerm}
        onClose={() => setNextTerm(null)}
        token={token}
        programs={programs.filter(p => (p.status || 'active') !== 'draft')}
        singleProgram={nextTerm?.singleProgram || null}
        onDone={fetchData}
      />

      {shareRoster && (
        <ShareRosterModal
          itemType="program"
          item={shareRoster}
          token={token}
          onClose={() => setShareRoster(null)}
        />
      )}
      </div>
    </AdminLayout>
  );
};

export default AdminContent;
